import type React from "react";
import type { FC } from "react";

interface InputProps {
  type?: "text" | "number" | "email" | "password" | "date" | "time" | string;
  id?: string;
  name?: string;
  placeholder?: string;
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  required?: boolean;
  autoComplete?: string;
  min?: string;
  max?: string;
  step?: number;
  disabled?: boolean;
  success?: boolean;
  error?: boolean;
  hint?: string;
  maxLength?: number;
}

const Input: FC<InputProps> = ({
  type = "text",
  id,
  name,
  placeholder,
  value,
  defaultValue,
  onChange,
  className = "",
  autoComplete,
  required,
  min,
  max,
  step,
  disabled = false,
  success = false,
  error = false,
  hint,
  maxLength,
}) => {
  let inputClasses = `h-[38px] w-full appearance-none rounded-[9px] border px-3 py-2 font-roboto text-[13px] font-light leading-[1.55] text-[var(--ink)] bg-[var(--page)] placeholder:text-[var(--mute2)] outline-none transition-colors ${className}`;

  if (disabled) {
    inputClasses += ` cursor-not-allowed border-[var(--rule)] bg-[var(--band)] text-[var(--mute)] opacity-60`;
  } else if (error) {
    inputClasses += ` border-[var(--crit)] focus:border-[var(--crit)] focus:ring-[3px] focus:ring-[var(--critbg)]`;
  } else if (success) {
    inputClasses += ` border-[var(--okt)] focus:border-[var(--okt)] focus:ring-[3px] focus:ring-[var(--okbg)]`;
  } else {
    inputClasses += ` border-[var(--rule)] hover:border-[var(--ghostbd)] focus:border-[var(--indigo)] focus:ring-[3px] focus:ring-[var(--indigotint)]`;
  }

  return (
    <div className="relative">
      <input
        type={type}
        id={id}
        name={name}
        placeholder={placeholder}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        autoComplete={autoComplete}
        min={min}
        max={max}
        required={required}
        step={step}
        disabled={disabled}
        maxLength={maxLength}
        className={inputClasses}
      />

      {hint && (
        <p
          className={`mt-1.5 text-[11px] ${
            error
              ? "text-[var(--critt)]"
              : success
              ? "text-[var(--okt)]"
              : "text-[var(--mute)]"
          }`}
        >
          {hint}
        </p>
      )}
    </div>
  );
};

export default Input;
